import React, { useState } from 'react';
import styled from 'styled-components';
import {
  FaCheckCircle, FaTimesCircle, FaPause, FaPlay, FaStop,
  FaChevronDown, FaChevronUp, FaSpinner
} from 'react-icons/fa';

const Container = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 12px;
  padding: ${p => p.theme.spacing(3)};
  border: 1.5px solid ${p =>
    p.$complete
      ? p.$failed > 0 ? p.theme.colors.error + '44' : '#66BB6A44'
      : p.theme.colors.primary + '44'};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: ${p => p.theme.spacing(2)};
  font-size: 1rem;
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};

  svg {
    font-size: 18px;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 10px;
  background: ${p => p.theme.colors.background};
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: ${p => p.theme.spacing(2)};
`;

const ProgressBarFill = styled.div`
  height: 100%;
  width: ${p => p.$pct}%;
  background: ${p => p.$complete
    ? p.$failed > 0 ? p.theme.colors.error : '#66BB6A'
    : p.theme.colors.primary};
  border-radius: 5px;
  transition: width 0.3s ease;
`;

const Stats = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing(3)};
  margin-bottom: ${p => p.theme.spacing(2)};
  flex-wrap: wrap;
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.88rem;

  .count {
    font-weight: 700;
    font-size: 1.1rem;
    color: ${p => p.color || p.theme.colors.textPrimary};
  }

  .label {
    color: ${p => p.theme.colors.textSecondary};
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing(1.5)};
  margin-top: ${p => p.theme.spacing(2)};
`;

const ActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 20px;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1.5px solid transparent;

  ${p => p.$variant === 'primary' && `
    background: ${p.theme.colors.primary};
    color: ${p.theme.colors.black || '#000'};
    &:hover { opacity: 0.9; }
  `}

  ${p => p.$variant === 'outline' && `
    background: transparent;
    color: ${p.theme.colors.textSecondary};
    border-color: ${p.theme.colors.inputGray}44;
    &:hover { border-color: ${p.theme.colors.textPrimary}; color: ${p.theme.colors.textPrimary}; }
  `}

  ${p => p.$variant === 'danger' && `
    background: transparent;
    color: ${p.theme.colors.error};
    border-color: ${p.theme.colors.error}44;
    &:hover { background: ${p.theme.colors.error}18; }
  `}
`;

const FailedSection = styled.div`
  margin-top: ${p => p.theme.spacing(2)};
  border-top: 1px solid ${p => p.theme.colors.inputGray}33;
  padding-top: ${p => p.theme.spacing(1.5)};
`;

const FailedToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: ${p => p.theme.colors.error};
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
`;

const FailedList = styled.div`
  margin-top: 8px;
  max-height: 160px;
  overflow-y: auto;
`;

const FailedItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 10px;
  font-size: 0.8rem;
  border-bottom: 1px solid ${p => p.theme.colors.inputGray}22;

  .lead-name {
    color: ${p => p.theme.colors.textPrimary};
    font-weight: 500;
  }

  .error-msg {
    color: ${p => p.theme.colors.error};
    max-width: 60%;
    text-align: right;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const BulkSmsProgress = ({
  total,
  sent,
  succeeded,
  failed,
  failedDetails = [],
  isPaused,
  isComplete,
  onPause,
  onResume,
  onCancel,
  onDone,
}) => {
  const [showFailed, setShowFailed] = useState(false);
  const pct = total > 0 ? Math.round((sent / total) * 100) : 0;

  const getTitle = () => {
    if (isComplete) return failed > 0 ? 'Sending Complete (with errors)' : 'All Messages Sent!';
    if (isPaused) return `Paused — ${sent} of ${total}`;
    return `Sending... ${sent} of ${total}`;
  };

  const getIcon = () => {
    if (isComplete) return failed > 0
      ? <FaTimesCircle color="#fa7f7f" />
      : <FaCheckCircle color="#66BB6A" />;
    if (isPaused) return <FaPause />;
    return <FaSpinner className="spinning" />;
  };

  return (
    <Container $complete={isComplete} $failed={failed}>
      <Title>
        {getIcon()}
        {getTitle()}
      </Title>

      <ProgressBarOuter>
        <ProgressBarFill $pct={pct} $complete={isComplete} $failed={failed} />
      </ProgressBarOuter>

      <Stats>
        <Stat>
          <span className="count">{sent}</span>
          <span className="label">/ {total} processed</span>
        </Stat>
        <Stat color="#66BB6A">
          <FaCheckCircle color="#66BB6A" />
          <span className="count" style={{ color: '#66BB6A' }}>{succeeded}</span>
          <span className="label">sent</span>
        </Stat>
        {failed > 0 && (
          <Stat color="#fa7f7f">
            <FaTimesCircle color="#fa7f7f" />
            <span className="count" style={{ color: '#fa7f7f' }}>{failed}</span>
            <span className="label">failed</span>
          </Stat>
        )}
      </Stats>

      <ButtonRow>
        {isComplete ? (
          <ActionBtn $variant="primary" onClick={onDone}>Done</ActionBtn>
        ) : (
          <>
            {isPaused ? (
              <ActionBtn $variant="primary" onClick={onResume}>
                <FaPlay size={11} /> Resume
              </ActionBtn>
            ) : (
              <ActionBtn $variant="outline" onClick={onPause}>
                <FaPause size={11} /> Pause
              </ActionBtn>
            )}
            <ActionBtn $variant="danger" onClick={onCancel}>
              <FaStop size={11} /> Cancel
            </ActionBtn>
          </>
        )}
      </ButtonRow>

      {failed > 0 && failedDetails.length > 0 && (
        <FailedSection>
          <FailedToggle onClick={() => setShowFailed(!showFailed)}>
            {showFailed ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
            {showFailed ? 'Hide' : 'Show'} failed messages ({failed})
          </FailedToggle>
          {showFailed && (
            <FailedList>
              {failedDetails.map((item, i) => (
                <FailedItem key={i}>
                  <span className="lead-name">{item.leadName}</span>
                  <span className="error-msg">{item.error}</span>
                </FailedItem>
              ))}
            </FailedList>
          )}
        </FailedSection>
      )}
    </Container>
  );
};

export default BulkSmsProgress;
